import { motion } from "framer-motion";
import { X } from "lucide-react";
import { useEffect, useRef } from "react";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import { cn } from "@/lib/utils";
import { FocusScope } from "@radix-ui/react-focus-scope";

import { useChatNotification } from "../api/chat-notification";

import { MessagesList } from "./messages-list";
import { SendMessageInput } from "./send-message-input";

export function ChatDrawer() {
  const isOpen = useVidyoStore((s) => s.chatDrawer.isOpen);
  const closeChatDrawer = useVidyoStore((s) => s.chatDrawer.close);
  const messagesLength = useVidyoStore((s) => s.chat.messages.length);
  const contentContainerRef = useRef<HTMLDivElement>(null);
  const messagesListRef = useRef<HTMLDivElement>(null);
  useChatNotification();

  // Close drawer when escape key is pressed
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeChatDrawer();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeChatDrawer]);

  useEffect(() => {
    if (messagesListRef.current) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  }, [messagesLength]);

  // Close settings drawer when component unmounts
  useEffect(() => {
    return () => {
      closeChatDrawer();
    };
  }, [closeChatDrawer]);

  return (
    <FocusScope
      trapped={isOpen}
      loop
      onMountAutoFocus={(e) => {
        e.preventDefault();
      }}
      onUnmountAutoFocus={(e) => {
        e.preventDefault();
      }}
      asChild
    >
      <motion.div
        className={cn(
          "fixed bottom-[var(--action-bar-height)] right-3 top-4 z-10 h-auto w-80",
          "rounded-md border border-border bg-background ring-4 ring-card",
        )}
        initial={{ x: 320 + 12 }}
        animate={{ x: isOpen ? 0 : 320 + 16 }} // 16 is the right-4 on this component
        transition={{ duration: 0.4, ease: "easeInOut" }}
        role="dialog"
        aria-hidden={!isOpen}
        aria-labelledby="in-call-messages-drawer-title"
        //@ts-expect-error inert is supported only react 19+
        inert={!isOpen ? "" : undefined}
      >
        <div className="flex h-full flex-col" ref={contentContainerRef}>
          <p
            className="px-4 py-4 text-sm text-foreground"
            id="in-call-messages-drawer-title"
          >
            In-call messages
          </p>

          <Button
            variant="ghost"
            size="icon"
            onClick={closeChatDrawer}
            className="absolute right-2 top-2"
          >
            <X className="size-4" />
          </Button>
          <Separator className="bg-border" />

          <ScrollArea
            className={`h-[${contentContainerRef.current?.clientHeight}px]`}
            ref={messagesListRef}
          >
            <p className="px-4 py-4 text-xs text-muted-foreground">
              When you leave the call, you won't be able to access this chat.
            </p>
            <MessagesList />
          </ScrollArea>
          <div className="mt-auto border-t border-border p-4">
            <SendMessageInput />
          </div>
        </div>
      </motion.div>
    </FocusScope>
  );
}
